import {convert, style} from '../util/index.js'
import {Path} from '../arl/index.js'

export const RawHandling = {

// gets the blu and viz file - only fails if blu is missing
async fetch() {

    // if the extension is json it is another node file
    if (this.blu.arl) {

        try {
            let bRaw = await this.blu.arl.get('json')

            if (isEntrypointRaw(bRaw)) {
                this.resolveEntrypointRaw(bRaw)
                bRaw = await this.blu.arl.get('json')
            }

            const vRaw = await this.viz.arl.get('json').catch(error => {
                // viz file not found - return null which makes the promise resolve successfully !
                console.warn(`Viz file failed to load, proceeding with null: ${error.message}`);
                return null;
            })

            // set the fresh bit 
            this.blu.is.fresh = true;
            this.viz.is.fresh = true;

            return {bRaw, vRaw};
        }
        catch( error ) {
            // This ONLY catches the rejection from pBlu, the mandatory promise.
            console.warn(`${this.blu.arl.getPath()} could not be found or is not valid.`, error);
            return {bRaw:null, vRaw:null};
        }
    }

    else if (this.bundle.arl) {

        const rawCode = await this.bundle.arl.get('text')
        .catch( error => {
            console.error(`${this.bundle.arl.getPath()} is not a valid bundle`, error)
        })

        //check
        if (!rawCode) return {bRaw:null, vRaw: null}

        // analyze the source to find the raw this
        const raw = this.analyzeJSLib(rawCode)

        // error if failed
        if (! raw) console.error(`model not found in bundle: ${this.bundle.arl.getPath()}`)

        // we are done 
        return {bRaw:raw, vRaw: null}
    }
    // it's one or the other !
    else return {bRaw:null, vRaw: null}
},

resolveEntrypointRaw(raw) {

    if (!raw?.model || typeof raw.model !== 'string') {
        throw new Error(`Entrypoint ${this.blu.arl.getPath()} requires a string "model" field`)
    }

    if (raw.version !== 1) {
        throw new Error(`Unsupported vmblu entrypoint version in ${this.blu.arl.getPath()}: ${raw.version}`)
    }

    const entrypointArl = this.blu.arl
    const modelArl = entrypointArl.resolve(raw.model)
    if (!modelArl) throw new Error(`Entrypoint ${entrypointArl.getPath()} could not resolve model path ${raw.model}`)

    const visualArl = raw.visual
        ? entrypointArl.resolve(raw.visual)
        : modelArl.resolve(defaultVizPath(modelArl))

    this.entrypoint = {
        arl: entrypointArl,
        raw
    }

    this.blu.arl = modelArl
    this.viz.arl = visualArl
},

// get the raw model from two files and combine in one
async getRaw() {

    // get both parts of a model
    const {bRaw:bRaw, vRaw:vRaw} = await this.fetch()

    // we need a blu file , and it should have at least a header
    if (!bRaw || !bRaw.header) return null;

    // make the header
    this.joinHeader(bRaw, vRaw)

    // combine bRaw and vRaw in bRaw
    if (vRaw?.root) this.joinNode(bRaw.root, vRaw.root) 

    // set raw in the model
    this.raw = bRaw

    // get the prompts for the nodes and pins
    if (this.is.withPrompts) await this.hydratePromptRepos?.(bRaw)

    // We also return the result
    return bRaw
},

async saveRaw() {

    const promptFiles = this.preparePromptReposForSave?.(this.raw) ?? []

    // now split the result in two parts
    const split = this.splitRaw(this.raw)

    // and return raw and the stringified results
    const blu = JSON.stringify(split.blu,null,2)
    const viz = JSON.stringify(split.viz,null,2)

    // save both parts of the model
    const saves = []
    if (blu) saves.push(saveFile(this.blu.arl, blu))
    if (viz) saves.push(saveFile(this.viz.arl, viz))

    await Promise.all(saves)
    await this.savePromptRepos?.(promptFiles)
},

// Splits raw into a part for the blu file and the viz file
splitRaw(raw) {

    // Split the raw model in a blu and viz section
    const sHeader = this.splitHeader(raw.header)
    const sRoot = this.splitNode(raw.root)

    const blu = {
        header: sHeader.blu
    }

    if (raw.imports) blu.imports = raw.imports;
    if (raw.factories) blu.factories = raw.factories;
    if (raw.types) blu.types = raw.types;
    blu.root = sRoot.blu

    const viz = {
        header: sHeader.viz,
        root: sRoot.viz
    }

    return { blu, viz}
},

splitHeader(rHeader) {

    const {version, created, saved, utc, runtime, runtimeSettings, agent, style, teams} = {...rHeader}
    const headerVersion = version ?? 'no version'
    const styleRgb = (typeof style === 'string') ? style : style?.rgb ?? style?.color ?? null

    const blu = {version: headerVersion, created, saved, utc, runtime}
    if (teams) blu.teams = JSON.parse(JSON.stringify(teams))
    if (runtimeSettings) blu.runtimeSettings = runtimeSettings
    if (agent) blu.agent = agent

    return { 
            blu,
            viz : {version: headerVersion, created, saved, utc, style: styleRgb}
    }
},

splitInterfaces(rawInterfaces) {

    // check
    if (! rawInterfaces?.length) return {blu:[], viz:[]}

    // map
    const blu = rawInterfaces.map( rif => {

        const pins = rif.pins.map( pin => {
                const bluPin = {    name: pin.name, 
                                    kind: pin.kind,
                                    contract: pin.contract
                                }
                if (pin.prompt?.length) bluPin.prompt = pin.prompt
                if (pin.tool) bluPin.tool = pin.tool
                if (pin.event) bluPin.event = pin.event
                return bluPin
            })
        return { interface: rif.interface, pins}
    })
    const viz = rawInterfaces.map( rif => {

        const pins = rif.pins.map( pin => convert.pinToString(pin))
        return {interface: convert.interfaceToString(rif), pins}
    })

    return {blu, viz}
},

splitNode(rNode) {

    const interfaces = this.splitInterfaces(rNode.interfaces)

    const blu = {
        kind: rNode.kind,
        name: rNode.name,
    }
    if (rNode.label) blu.label = rNode.label
    if (rNode.team) blu.team = rNode.team
    if (rNode.prompt) blu.prompt = rNode.prompt;
    if (rNode.promptRepo && rNode.kind !== 'dock') {
        blu.promptRepo = rNode.promptRepo.makeRaw ? rNode.promptRepo.makeRaw(this.blu.arl) : rNode.promptRepo;
    }
    if (rNode.testRepo && rNode.kind !== 'dock') {
        blu.testRepo = rNode.testRepo.makeRaw ? rNode.testRepo.makeRaw(this.blu.arl) : rNode.testRepo;
    }
    if (rNode.probes) blu.probes = rNode.probes;

    const viz = {
        kind: rNode.kind,
        name: rNode.name,
        rect: convert.rectToString(rNode.rect),     
    }

    if (rNode.kind == 'dock') {
        blu.link = rNode.link;
        if (interfaces.blu.length) blu.interfaces = interfaces.blu;
        if (rNode.sx) blu.sx = rNode.sx;
        if (rNode.dx) blu.dx = rNode.dx;

        if (interfaces.viz.length) viz.interfaces = interfaces.viz;
    }
    else if (rNode.kind == 'source') {
        blu.factory = rNode.factory;     
        if (interfaces.blu.length) blu.interfaces = interfaces.blu;
        if (rNode.sx) blu.sx = rNode.sx;
        if (rNode.dx) blu.dx = rNode.dx; 

        if (interfaces.viz.length) viz.interfaces = interfaces.viz;
    }
    else if (rNode.kind == 'group') {

        const splitNodes = rNode.nodes.map( rNode => this.splitNode(rNode))

        if (interfaces.blu.length) blu.interfaces = interfaces.blu;
        if (rNode.sx) blu.sx = rNode.sx;
        if (rNode.dx) blu.dx = rNode.dx;
        if (splitNodes.length) blu.nodes = splitNodes.map( node => node.blu )
        if (rNode.connections) blu.connections = rNode.connections

        if (rNode.view) viz.view = convert.toViewStrings(rNode.view) 
        if (interfaces.viz.length) viz.interfaces = interfaces.viz;
        if (rNode.pads) viz.pads = rNode.pads.map( pad => convert.padToString(pad))
        if (splitNodes.length) viz.nodes = splitNodes.map( node => node.viz )
        const legacyBuses = rNode.buses?.map(bus => ({...bus, floating: true})) ?? []
        const cables = rNode.cables ?? []
        if (legacyBuses.length || cables.length) viz.cables = [...legacyBuses, ...cables]
        if (rNode.routes) viz.routes = rNode.routes
    }

    return {blu, viz}
},


joinHeader(bRaw, vRaw) {
    const vStyle = vRaw?.header?.style
    bRaw.header.style = (typeof vStyle === 'string') ? vStyle : style.rgb;
    if (!bRaw.header.teams) {
        bRaw.header.teams = {
            default: {
                color: validTeamColor(bRaw.header.style) ? bRaw.header.style : style.rgb
            }
        }
    }
    // this.header.cook(arl, bRaw.header)
},

joinNode(bNode, vNode) {

    // The rectangle and the view of the node
    bNode.rect = vNode.rect ? convert.stringToRect(vNode.rect) : null;

    // The interfaces
    if (bNode.interfaces?.length && vNode.interfaces?.length) this.joinInterfaces(bNode, vNode)

    // group node specific
    if (bNode.kind == "group") {

        // The node view
        bNode.view = vNode.view ? convert.fromViewStrings(vNode.view) : null;

        // The pads
        bNode.pads = vNode.pads ? vNode.pads.map( pad => convert.stringToPad(pad)) : [];

        // The nodes
        bNode.nodes = bNode.nodes?.map( bn => {
            const vn = vNode.nodes?.find(vn => bn.name == vn.name)
            if (vn) this.joinNode(bn, vn) 
            return bn;
        })

        bNode.buses = vNode.buses
        bNode.cables = vNode.cables

        // copy the routes
        bNode.routes = vNode.routes
    }
},

joinInterfaces(bNode, vNode) {

    // first convert strings
    vNode.interfaces = vNode.interfaces.map( iface => {
        const serializedInterface = iface.interface ?? iface.name
        const vif = {...convert.stringToInterface(serializedInterface)}
        vif.pins = iface.pins.map( pin => convert.stringToPin(pin))
        return vif
    })

    // combine b and v
    bNode.interfaces = bNode.interfaces.map( bInterface => {
        const vInterface = vNode.interfaces.find( vInterface => vInterface.text == bInterface.interface)
        if (vInterface) {
            bInterface.pins = bInterface.pins.map( pin => {
                const vpin = vInterface.pins.find( vpin => vpin.name == pin.name)
                return vpin ? {...pin, wid:vpin.wid, left: vpin.left} : {...pin, wid:0, left: false};
            })
        }
        return bInterface
    })
},

// A function that adjusts raw references for the content from the clipboard
handleRawPaths(imports, raw) {

    // this is the raw clipboard 
    // const target = {
    //     origin: this.origin.getArl()?.getFullPath(),
    //     header: this.origin.header.copyWithoutStyle(),
    //     what: this.selection.what,
    //     rect: this.selection.rect ? convert.rectToString(this.selection.rect) : null,
    //     viewPath: this.selection.viewPath,
    //     root: null,
    //     widgets: null
    // }

    // Only in the root adjustments need to be made..
    if (!raw.root) return;

    // notation
    const oldRef = raw.origin
    const newRef = this.fullPath()

    // if the refs are the same - nothing to do
    if (oldRef == newRef) return;

    // recursive helper function
    const checkPaths = (rawNode, fn) => {
        if (rawNode.link) {
            rawNode.link.path = fn(rawNode.link.path, true);
            imports.push(rawNode.link.path)
        }
        else if (rawNode.factory) {
            rawNode.factory.path = fn(rawNode.factory.path, false)
        }
        else if (rawNode.nodes) {
            for( const subNode of rawNode.nodes) checkPaths( subNode, fn)
        }
    }

    // helper function
    const adjustPath = (userPath, isLink) => {
        const absolutePath = (isLink && ! userPath?.length) ? oldRef : Path.absolute(userPath, oldRef)
        return Path.relative(absolutePath, newRef)     
    }

    // check all paths recursively
    checkPaths(raw.root, adjustPath)

    // return the adjusted
    return;
},

// A function that makes the top level nodes links
rawAsLinks(imports, raw) {

    // We need to adapt already existing links ! 
    const oldRef = raw.origin
    const newRef = this.fullPath()
  
    // we only save the origin once 
    let saveOrigin = true

    // set all the top-level nodes as links
    for(const rawNode of raw.root.nodes) {

        // don't change nodes that are already links but we might have to adapt the link !
        if (rawNode.kind === 'dock') {
            if (oldRef !== newRef) {
                const link = rawNode.link
                const absolutePath = (! link.path?.length) ? oldRef : Path.absolute(link.path, oldRef)
                link.path = Path.relative(absolutePath, newRef)  
                imports.push(link.path)
            }
        }
        else {
            rawNode.kind = 'dock'
            rawNode.link = { path: raw.origin, node: rawNode.name}

            // save origin just once
            if (saveOrigin) {
                imports.push(raw.origin)
                saveOrigin = false
            }
        }
    }

    // return the adjusted
    return;
},

// Finds the model text in the library file...
analyzeJSLib(rawCode) {

    // find the libname in the code
    let start = rawCode.indexOf('"{\\n')
    let end = rawCode.indexOf('\\n}";', start)

    // check
    if (start < 0 || end < 0) return null

    // get the part between starting and ending bracket
    let rawText = rawCode.slice(start+1,end+3)

    // allocate an array for the resulting text
    const cleanArray = new Array(rawText.length)
    let iClean = 0
    let iRaw = 0

    // remove all the scape sequences
    while (iRaw < rawText.length) {

        // get the first character
        const char1 = rawText.charAt(iRaw++)

        // if not a backslash, just copy
        if (char1 != '\\') {
            cleanArray[iClean++] = char1
        }
        else {

            // get the character that has been escaped 
            const char2 = rawText.charAt(iRaw++)

            // handle the different escape sequences
            if (char2 == 'n') 
                cleanArray[iClean++] = '\n'
            else if (char2 == '"') 
                cleanArray[iClean++] = '"'
        }
    }

    // combine all characters into a new string
    const cleanText = cleanArray.join('')

    // and parse
    return JSON.parse(cleanText)
},

}

function isEntrypointRaw(raw) {
    return raw?.kind === 'vmblu.entrypoint'
}

function defaultVizPath(modelArl) {
    const split = Path.split(modelArl.getPath())
    return split.name + (split.kind ?? '') + '.viz'
}

function validTeamColor(color) {
    return typeof color === 'string' && /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(color)
}

async function saveFile(arl, body) {
    try {
        const result = await arl.save(body)
        if (result === null || result === false) throw new Error('File write returned failure')
        return result
    }
    catch (error) {
        console.error(`Failed to save ${arl.getPath()}:`, error)
        throw error
    }
}
